import React from 'react';

export interface ShippingOption {
  id: string;
  title: string;
  price: number;
  priceLabel?: string;
  /**
   * HL-11357: the fee before a Care+ plan bonus waived it. Set only when the bonus covers this
   * option's fee in full, and the row then reads "5,90 € 0 €" — struck original, charged amount.
   */
  originalPrice?: number;
  description?: React.ReactNode;
  isDisabled?: boolean;
  disabledReason?: string;
}

interface ShippingOptionsSelectorProps {
  options: ShippingOption[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  label?: string;
  footerDescription?: React.ReactNode;
  /** Care+ accent: orange radio instead of the default green. */
  orange?: boolean;
}

const formatPrice = (price: number, priceLabel?: string): string => {
  if (priceLabel) return priceLabel;
  return `${price.toFixed(2).replace('.', ',')} €`;
};

const ShippingOptionsSelector: React.FC<ShippingOptionsSelectorProps> = ({ options, selectedId, onSelect, label, footerDescription, orange = false }) => {
  return (
    <div className="mb-5">
      {label && (
        <span className="font-roboto font-semibold text-[13px] text-[#98a0af] mb-2 block">{label}</span>
      )}
      <div className="flex flex-col gap-[8px]">
        {options.map((option) => {
          const isSelected = option.id === selectedId;
          const isDisabled = option.isDisabled || false;

          return (
            <div
              key={option.id}
              className={`bg-white border border-solid border-[#b5bdcc] rounded-[6px] overflow-hidden ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
              onClick={() => !isDisabled && onSelect(option.id)}
            >
              <div className="flex items-center p-[20px]">
                <div className="flex items-center gap-[4px] flex-1 min-w-0">
                  <div className="shrink-0 size-[24px] flex items-center justify-center">
                    {isSelected ? (
                      <div className={`w-[20px] h-[20px] border-2 border-solid ${orange ? 'border-[#f08e00]' : 'border-[#00734a]'} rounded-full flex items-center justify-center`}>
                        <div className={`w-[12px] h-[12px] ${orange ? 'bg-[#f08e00]' : 'bg-[#269c6e]'} rounded-full`} />
                      </div>
                    ) : (
                      <div className="w-[20px] h-[20px] border border-solid border-[#b5bdcc] rounded-full" />
                    )}
                  </div>
                  <span className={`font-poppins font-medium text-[14px] ${isDisabled ? 'text-[#98a0af]' : 'text-[#0d2c54]'}`}>
                    {option.title}
                  </span>
                </div>
                <span className="font-poppins font-medium text-[16px] ml-4 flex items-baseline gap-[8px] whitespace-nowrap">
                  {option.originalPrice !== undefined && (
                    <span className="text-[14px] text-[#98a0af] line-through">
                      {formatPrice(option.originalPrice)}
                    </span>
                  )}
                  <span className={option.originalPrice !== undefined ? 'text-[#269c6e]' : 'text-[#616975]'}>
                    {formatPrice(option.price, option.priceLabel)}
                  </span>
                </span>
              </div>
              {isSelected && option.description && (
                <div className="px-[20px] py-[19px] border-t border-solid border-[#dbe1eb]">
                  <span className="font-roboto text-[13px] text-[#656d79] leading-[19.5px]">{option.description}</span>
                </div>
              )}
              {isDisabled && option.disabledReason && (
                <div className="px-[20px] pb-[10px]">
                  <span className="font-roboto text-[12px] text-[#98a0af]">{option.disabledReason}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {footerDescription && (
        <div className="font-roboto text-[13px] text-[#656d79] leading-[19.5px] mt-[24px]">
          {footerDescription}
        </div>
      )}
    </div>
  );
};

export default ShippingOptionsSelector;
